import { QueryClient } from "@tanstack/react-query";

import { WaitlistEntry, WaitlistStatus } from "@/types/waitlist";

import { requestData } from "@/api/api-config";
import {
  WaitlistCacheScope,
  WaitlistFilters,
  isCurrentWaitlistScope,
  isWaitlistEntry,
  readWaitlistCache,
  replaceWaitlistCache,
  setWaitlistRecoveryStatus,
  waitlistQueryKeys,
} from "@/query/waitlist-cache";

/** One deduplicated ordinary primary GET on reconnect or explicit recovery Retry. */
export function recoverWaitlistCache(
  client: QueryClient,
  scope: WaitlistCacheScope,
): Promise<void> {
  if (!isCurrentWaitlistScope(client, scope)) return Promise.resolve();
  if (scope.recovery) return scope.recovery;
  const targets = new Set(
    client
      .getQueryCache()
      .findAll()
      .filter(
        (query) =>
          query.queryKey[0] === "waitlist" &&
          (query.queryKey[1] === "list" ||
            (query.queryKey[1] === "detail" && !!query.queryKey[2])) &&
          (query.state.data !== undefined ||
            query.state.fetchStatus === "fetching" ||
            query.state.status === "error"),
      ),
  );
  if (!targets.size) return Promise.resolve();
  // A detail may have changed status while disconnected. Only list-only
  // recovery can safely limit the snapshot to the requested statuses.
  const status = [...targets].some((query) => query.queryKey[1] === "detail")
    ? Object.values(WaitlistStatus)
    : [
        ...new Set(
          [...targets].flatMap((query) => {
            const filters = query.queryKey[2] as WaitlistFilters;
            return filters.status?.length
              ? filters.status
              : [WaitlistStatus.WAITING];
          }),
        ),
      ].sort();
  setWaitlistRecoveryStatus(client, scope, {
    pending: true,
    error: scope.recoveryStatus.error,
  });
  scope.recovery = (async () => {
    try {
      const rows = await readWaitlistCache(
        client,
        scope,
        waitlistQueryKeys.all,
        async () => {
          const result = await requestData<WaitlistEntry[]>(
            "waitlist",
            "get",
            { searchParams: { status } },
            false,
          );
          if (!Array.isArray(result) || !result.every(isWaitlistEntry))
            throw new Error("Invalid waitlist recovery response");
          // Older ordinary GETs must not overwrite this recovered result.
          // Keep the recovery read's change overlay active during cancellation.
          if (isCurrentWaitlistScope(client, scope))
            await client.cancelQueries({
              predicate: (query) => targets.has(query),
            });
          return result;
        },
      );
      replaceWaitlistCache(client, scope, rows, targets);
      setWaitlistRecoveryStatus(client, scope, { pending: false, error: null });
    } catch (error) {
      if (!isCurrentWaitlistScope(client, scope)) return;
      setWaitlistRecoveryStatus(client, scope, {
        pending: false,
        error: error instanceof Error ? error : new Error(String(error)),
      });
      throw error;
    } finally {
      scope.recovery = null;
    }
  })();
  return scope.recovery;
}
